import { Link, Outlet } from 'react-router-dom';
import { GraduationCap, LogOut } from 'lucide-react';
import { useCurrentUser, useLogout } from '../../hooks/useAuth';
import { ThemeToggle } from './ThemeToggle';
import { FontSizeControl } from './FontSizeControl';
import { OfflineIndicator } from './OfflineIndicator';

/**
 * Layout for the training portal (training.corrigenda.co.uk). Deliberately
 * its own shell rather than AppShell: long-tenure staff land here, so there
 * is no sidebar, no mobilisation switcher, and none of the onboarding /
 * welcome framing. Just a training wordmark, theme toggle and sign-out over
 * a single bounded scroll area. The header is full-width because the portal
 * has no primary nav rail to carry the brand.
 */
export function TrainingShell() {
  const { data: user, isLoading } = useCurrentUser();
  const logout = useLogout();

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-bg text-fg">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line/10 bg-surface px-3 shadow-token-sm sm:px-4 print:hidden">
        <Link
          to="/"
          className="flex shrink-0 items-center gap-2 rounded-md text-fg transition-colors hover:text-accent"
        >
          <span
            aria-hidden
            className="flex h-8 w-8 items-center justify-center rounded-token-sm text-fg-on-accent shadow-token-accent ring-1 ring-accent/30"
            style={{
              backgroundImage:
                'linear-gradient(135deg, rgb(var(--accent)) 0%, #6366f1 100%)',
            }}
          >
            <GraduationCap className="h-4 w-4" />
          </span>
          <span className="text-sm font-semibold tracking-tight text-fg">
            Corrigenda Training
          </span>
        </Link>

        <div className="ml-auto flex items-center gap-2 text-sm">
          <FontSizeControl />
          <ThemeToggle />
          {user ? (
            <span
              className="hidden h-9 items-center rounded-token-sm bg-surface-2 px-2.5 text-xs font-medium text-fg-muted md:inline-flex"
              title={user.email}
            >
              {user.display_name}
            </span>
          ) : null}
          {user ? (
            <button
              type="button"
              onClick={() => logout.mutate()}
              aria-label="Sign out"
              title="Sign out"
              className="inline-flex h-9 min-h-touch items-center gap-1.5 rounded-token-sm border border-line-strong/15 bg-surface px-2.5 text-xs font-medium text-fg-muted transition-colors hover:bg-surface-2"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          ) : null}
        </div>
      </header>

      <OfflineIndicator />

      {/* Single bounded scroll container for the page content, mirroring
          AppShell so pages behave the same on a hard refresh. isLoading
          guards the brief gap before the session probe resolves. */}
      <div className="min-h-0 flex-1 overflow-y-auto [&>main]:!min-h-0">
        {isLoading ? null : <Outlet />}
      </div>
    </div>
  );
}
